"use client";

// SPEC-092 D16 — единый двухшаговый вход для ВСЕХ ролей (магазины, офис,
// владельцы). Геометрия 1:1 с макетами Pencil aY7T7 (шаг 1 — телефон) и shXbD
// (шаг 2 — шесть клеток). Пароль — РОВНО 6 цифр у всех.
//
// Все координаты внутри карточки 483×1043 — абсолютные, ровно как в макете:
// шаг 1 — блок телефона y=426, кнопка x=90 y=515 (304×56);
// шаг 2 — «назад» 24/24 (44×36), телефон 100/31, подпись y=472,
//         ряд клеток x=67.5 y=506 (48×72, gap 12), нижняя строка y=578 (483×70).
//
// Отличие от макета только в текстах: SMS-кода мы не шлём (вводится пароль),
// поэтому кнопка шага 1 — «Далее», подпись шага 2 — «Пароль», а на месте
// строки таймера показываем ошибку входа. Отправка — по вводу шестой цифры
// (кнопки «Войти» в макете нет).
//
// Конвейер отправки — прежний офисный: CSRF-бутстрап + один повтор на 403,
// POST /api/auth/login {phone, password}; успех → landingHref(operations).
// Срок сессии и ограничитель попыток (3 неудачи → 429 + Retry-After) — на сервере.

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowLeft } from "lucide-react";

import { bootstrapCsrf, getCsrfCookieValue } from "@/lib/csrf";
import { formatStorePhone } from "@/lib/store-logins-api";
import { loginSubmit } from "./onSubmit";

const PASSWORD_LENGTH = 6;
const PHONE_DIGITS = 10;

/**
 * Вытащить 10 национальных цифр из того, что напечатал/вставил пользователь.
 *
 * В самом поле «+7» нет (это отдельная надпись), так что обычный набор просто
 * копится цифра за цифрой. Срезать код страны нужно лишь когда человек вставил
 * из буфера ПОЛНЫЙ номер («+7 909…», «8 909…»): правило то же, что на бэкенде
 * (backoffice/telegram/phone.py) — ведущие «7»/«8» отбрасываются только при 11
 * цифрах. Поэтому номера, начинающиеся на семёрку, вводятся нормально.
 */
function extractPhoneDigits(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  const national =
    digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"))
      ? digits.slice(1)
      : digits;
  return national.slice(0, PHONE_DIGITS);
}

/** «9099040077» → «909 904 00 77» (как в макете шага 1). */
function formatPhoneDigits(digits: string): string {
  const parts = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
  ].filter(Boolean);
  return parts.join(" ");
}

export default function LoginPage() {
  const router = useRouter();
  const [step, setStep] = useState<1 | 2>(1);
  const [phoneDigits, setPhoneDigits] = useState("");
  const [code, setCode] = useState<string[]>(() =>
    Array<string>(PASSWORD_LENGTH).fill(""),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cellRefs = useRef<(HTMLInputElement | null)[]>([]);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  // Замок отправки — именно ref, а не состояние: submit зовётся синхронно из
  // writeDigits, а setSubmitting применится только к следующему рендеру, так что
  // на состоянии два быстрых события (дребезг ввода, вставка) успели бы
  // проскочить в два POST-а.
  const inFlight = useRef(false);
  // Зеркало клеток, обновляемое синхронно, — источник правды внутри обработчиков.
  const codeRef = useRef(code);
  // Заявка «вернуть курсор в первую клетку» — ref, а не состояние: снимать её
  // через setState прямо в эффекте значило бы лишний каскад рендеров.
  const refocusCells = useRef(false);

  // Фокус возвращаем ПОСЛЕ отрисовки: в момент ответа сервера клетка ещё
  // disabled={submitting}, и браузер молча проглотил бы focus() — на телефоне это
  // выглядит как «клавиатура закрылась, а ввести пароль заново нечем».
  useEffect(() => {
    if (submitting || !refocusCells.current) return;
    refocusCells.current = false;
    cellRefs.current[0]?.focus();
  }, [submitting]);

  /** Записать клетки разом: и в состояние (для отрисовки), и в ref (для логики). */
  function setCells(next: string[]) {
    codeRef.current = next;
    setCode(next);
  }

  const phone = `+7${phoneDigits}`;
  const phoneReady = phoneDigits.length === PHONE_DIGITS;

  /**
   * Ввод телефона с сохранением места курсора.
   *
   * Значение поля переформатируется на каждый ввод («9099040077» → «909 904 00
   * 77»), поэтому браузерная позиция курсора после правки в СЕРЕДИНЕ строки
   * съезжает — следующая цифра улетала бы в конец. Считаем позицию в цифрах
   * (сколько их слева от курсора) и после отрисовки ставим курсор туда же.
   */
  function handlePhoneChange(event: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    const el = event.target;
    const caret = el.selectionStart ?? el.value.length;
    const digitsLeft = el.value.slice(0, caret).replace(/\D/g, "").length;
    const digits = extractPhoneDigits(el.value);
    setPhoneDigits(digits);

    // Перевести «столько-то цифр слева» обратно в позицию внутри отформатированной
    // строки: идём по ней и считаем цифры.
    const formatted = formatPhoneDigits(digits);
    let seen = 0;
    let pos = formatted.length;
    for (let i = 0; i < formatted.length; i += 1) {
      if (seen === digitsLeft) {
        pos = i;
        break;
      }
      if (/\d/.test(formatted[i])) seen += 1;
    }
    if (seen === digitsLeft && pos === formatted.length) pos = formatted.length;
    requestAnimationFrame(() => el.setSelectionRange(pos, pos));
  }

  function goToPassword() {
    if (!phoneReady) return;
    setError(null);
    setCells(Array<string>(PASSWORD_LENGTH).fill(""));
    setStep(2);
  }

  function goBack() {
    setError(null);
    setStep(1);
  }

  async function submit(password: string) {
    if (password.length !== PASSWORD_LENGTH || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    let failed = false;
    let navigated = false;
    // Страховка от непредвиденного сбоя внутри отправки (нечитаемая кука,
    // не загрузившийся чанк): без неё бросок оставлял экран замороженным —
    // клетки disabled, ни ошибки, ни запроса, и выйти можно только перезагрузкой.
    try {
      await loginSubmit(
        { phone, password },
        {
          fetch: globalThis.fetch.bind(globalThis),
          getCsrfCookieValue,
          bootstrapCsrf,
          navigate: (path) => {
            navigated = true;
            // router.push возвращает void и не подтверждает, что переход состоялся;
            // упасть он тоже может — тогда экран остался бы заблокированным навсегда.
            try {
              router.push(path);
            } catch {
              navigated = false;
            }
          },
          setError: (message) => {
            failed = true;
            setError(message);
          },
        },
      );
    } catch {
      failed = true;
      setError("Не удалось выполнить вход. Обновите страницу и попробуйте ещё раз.");
    }

    if (!failed) {
      // Успех — замок держим: до ухода со страницы экран ещё жив, и повторная
      // вставка цифр успела бы отправить второй POST (лишняя сессия + сожжённая
      // попытка в ограничителе). Но если увести не удалось, замок надо снять,
      // иначе форма мертва до перезагрузки.
      if (navigated) return;
      inFlight.current = false;
      setSubmitting(false);
      setError("Не удалось открыть страницу. Попробуйте ещё раз.");
      return;
    }
    inFlight.current = false;
    // Пароль не подошёл — чистим клетки и заявляем возврат курсора в первую.
    // Заявку поднимаем ДО setSubmitting(false): её читает эффект, который
    // сработает на снятии submitting, когда клетка уже не disabled.
    setCells(Array<string>(PASSWORD_LENGTH).fill(""));
    refocusCells.current = true;
    setSubmitting(false);
  }

  /** Записать цифры начиная с клетки `index` (и обычный ввод, и вставка). */
  function writeDigits(index: number, digits: string) {
    if (!digits) return;
    // Считаем от codeRef, а не от состояния: state обновится только к следующему
    // рендеру, поэтому два события в одном тике (вставка + onChange) на нём
    // затирали бы друг друга, а решение «код набран» опаздывало бы на шаг.
    const next = [...codeRef.current];
    for (let i = 0; i < digits.length && index + i < PASSWORD_LENGTH; i += 1) {
      next[index + i] = digits[i];
    }
    setCells(next);
    const landing = Math.min(index + digits.length, PASSWORD_LENGTH - 1);
    cellRefs.current[landing]?.focus();
    const filled = next.join("");
    if (filled.length === PASSWORD_LENGTH) void submit(filled);
  }

  function handleCellChange(index: number, raw: string) {
    setError(null);
    const digits = raw.replace(/\D/g, "");
    // Пусто — это стирание (Delete, вырезание, мобильная клавиатура/IME, которая
    // сообщает удаление только через change). Раньше мы просто выходили, и старая
    // цифра возвращалась обратно — стереть можно было лишь Backspace'ом.
    if (!digits) {
      const next = [...codeRef.current];
      next[index] = "";
      setCells(next);
      return;
    }
    writeDigits(index, digits);
  }

  function handleCellKeyDown(
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) {
    if (event.key !== "Backspace") return;
    event.preventDefault();
    setError(null);
    const next = [...codeRef.current];
    // Клетка пуста — стираем предыдущую и возвращаемся в неё.
    const emptyHere = next[index] === "" && index > 0;
    next[emptyHere ? index - 1 : index] = "";
    setCells(next);
    if (emptyHere) cellRefs.current[index - 1]?.focus();
  }

  function handlePaste(
    index: number,
    event: React.ClipboardEvent<HTMLInputElement>,
  ) {
    const digits = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!digits) return;
    event.preventDefault();
    setError(null);
    writeDigits(index, digits.slice(0, PASSWORD_LENGTH - index));
  }

  return (
    <main className="flex min-h-dvh items-center justify-center overflow-y-auto bg-white px-4 py-12">
      {/* Ширина — 483 как в макете, по высоте карточка вписывается в окно с
          отступами сверху и снизу (как модалка Самоката), не выше 1043 из
          макета. Блоки внутри стоят на своих долях высоты макета:
          426/1043 = 40.8%, 515/1043 = 49.4%, 472/1043 = 45.3%,
          506/1043 = 48.5%, 578/1043 = 55.4%.
          min-h-[720px]: ниже этой высоты доли начинают накладывать блок телефона
          на кнопку, поэтому вместо сплющивания страница прокручивается. */}
      <div className="relative box-border h-[calc(100dvh-96px)] max-h-[1043px] min-h-[720px] w-full max-w-[483px] overflow-hidden rounded-[32px] border border-[#E5E7EB] bg-white font-sans shadow-[0_24px_64px_#0000001F]">
        {step === 1 ? (
          <>
            <div className="absolute left-0 top-[40.8%] flex w-full flex-col items-center px-6">
              <label
                htmlFor="login-phone"
                className="text-[14px] font-normal text-[#6B7280]"
              >
                Телефон
              </label>
              {/* «+7» — отдельная надпись, а НЕ часть значения поля: иначе при
                  поклавишном вводе семёрка префикса попадала бы обратно в номер
                  на каждом нажатии (набор 909… превращался в 777…). В поле
                  живут только десять национальных цифр. */}
              <div className="flex items-center justify-center gap-2 text-[32px] font-medium leading-[1.2] text-[#111827]">
                <span aria-hidden="true">+7</span>
                <input
                  id="login-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  autoFocus
                  placeholder="000 000 00 00"
                  ref={phoneRef}
                  value={formatPhoneDigits(phoneDigits)}
                  onChange={handlePhoneChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") goToPassword();
                  }}
                  className="w-[13ch] bg-transparent text-[32px] font-medium leading-[1.2] text-[#111827] caret-[#007052] outline-none placeholder:text-[#D1D5DB]"
                />
              </div>
            </div>

            {/* Кнопка центрируется от середины карточки, а не от 90px макета:
                на экране 320px фикс left-[90px] + w-[304px] уезжал за правый
                край и обрезался (106px). */}
            <button
              type="button"
              onClick={goToPassword}
              disabled={!phoneReady}
              className="absolute left-1/2 top-[49.4%] h-[56px] w-[calc(100%-32px)] max-w-[304px] -translate-x-1/2 rounded-[28px] bg-[#007052] text-[16px] font-medium text-white transition-colors hover:bg-[#005E45] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Далее
            </button>

            {error && (
              <div
                role="alert"
                className="pointer-events-none absolute left-0 top-[56.6%] flex w-full justify-center px-10 text-center text-[14px] text-[#DC2626]"
              >
                {error}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Шапка — в потоке, а не по пиксельным координатам макета: иначе на
                узком экране «Назад» уезжает под обрез карточки. */}
            <div className="absolute left-0 top-[24px] flex w-full items-center gap-[32px] px-6">
              <button
                type="button"
                onClick={goBack}
                aria-label="Назад"
                className="flex h-[36px] w-[44px] shrink-0 items-center justify-center rounded-[18px] bg-[#F2F2F2] transition-colors hover:bg-[#E5E5E5]"
              >
                <ArrowLeft className="size-5 text-[#404040]" />
              </button>
              <span className="text-[18px] font-medium text-[#111827]">
                {formatStorePhone(phone)}
              </span>
            </div>

            {/* Подпись + клетки + ошибка — ОДИН блок в потоке (привязан к 45.3%
                из макета). Раньше каждый висел на своей доле высоты, и полоса
                ошибки наезжала на клетки: клетки высотой 72px фиксированы, а
                доли сжимаются вместе с карточкой. */}
            <div className="absolute left-0 top-[45.3%] flex w-full flex-col items-center px-4">
              <span className="text-[14px] font-normal text-[#6B7280]">
                Пароль
              </span>

              {/* Клетки: 48×72 из макета, на узких экранах ужимаются, чтобы
                  крайние не срезались. */}
              <div className="mt-[34px] flex w-full justify-center gap-[8px] sm:gap-[12px]">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      cellRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    autoFocus={index === 0}
                    maxLength={1}
                    value={digit}
                    aria-label={`Цифра ${index + 1}`}
                    disabled={submitting}
                    onChange={(e) => handleCellChange(index, e.target.value)}
                    onKeyDown={(e) => handleCellKeyDown(index, e)}
                    onPaste={(e) => handlePaste(index, e)}
                    className="h-[72px] w-full max-w-[48px] min-w-0 rounded-[12px] bg-[#F2F2F2] text-center text-[24px] font-medium text-[#111827] caret-[#007052] outline-none"
                  />
                ))}
              </div>

              <div
                role="alert"
                className="flex min-h-[70px] w-full items-center justify-center px-6 text-center text-[16px] font-normal text-[#DC2626]"
              >
                {error}
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
